import { useState } from 'react'
import { fbt, useLocaleContext } from 'fbtee'
import { CalendarIcon } from 'lucide-react'
import { toBcp47 } from '@shared/dates'
import { dateToISO } from '@/lib/formatDate'
import { Button } from './button'
import { Calendar } from './calendar'
import { Popover, PopoverContent, PopoverTrigger } from './popover'
import { cn } from '@/lib/utils'

function parseISO(value: string): Date | undefined {
  if (!value) return undefined
  const dateOnly = value.includes('T') ? value.slice(0, value.indexOf('T')) : value
  const [y, m, d] = dateOnly.split('-').map(Number)
  if (!y || !m || !d) return undefined
  return new Date(y, m - 1, d)
}

interface DatePickerProps {
  id?: string
  value: string
  onChange: (value: string) => void
  placeholder?: React.ReactNode
  className?: string
  // Raise the calendar popover's stacking — needed when the DatePicker lives
  // inside another popover that sets a high z-index (e.g. the clock override).
  contentClassName?: string
}

export function DatePicker({ id, value, onChange, placeholder, className, contentClassName }: DatePickerProps) {
  const [open, setOpen] = useState(false)
  const { locale } = useLocaleContext()
  const tag = toBcp47(locale)
  const date = parseISO(value)

  const formatted = date
    ? date.toLocaleDateString(tag, { year: 'numeric', month: 'short', day: 'numeric' })
    : null

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          className={cn(
            'w-full cursor-pointer justify-start font-normal',
            !formatted && 'text-muted-foreground',
            className,
          )}
        >
          <CalendarIcon className="size-3.5 mr-2 shrink-0" />
          {formatted ?? placeholder ?? fbt('Pick a date', 'Default placeholder for an unset date picker')}
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn('w-auto p-0', contentClassName)} align="start">
        <Calendar
          mode="single"
          defaultMonth={date}
          selected={date}
          onSelect={d => {
            if (d) onChange(dateToISO(d))
            setOpen(false)
          }}
          autoFocus
        />
      </PopoverContent>
    </Popover>
  )
}
